<script setup>
import { ref, reactive, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { convertToCategorySlug } from 'dashboard/helper/commons.js';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { buildPortalURL } from 'dashboard/helper/portalHelper';
import { isValidSlug } from 'shared/helpers/Validators';

import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'dashboard/components-next/polaris/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';

const emit = defineEmits(['create']);

const { t } = useI18n();
const store = useStore();

// A short form, so a dialog (D-G6), drawn with the kit's Dialog + Field + Input (phase 6.1). Callers still open it
// through the exposed `dialogRef.open()`, as they did with the woot Dialog.
const isOpen = ref(false);
const dialogRef = {
  open: () => {
    isOpen.value = true;
  },
  close: () => {
    isOpen.value = false;
  },
};

const isCreatingPortal = useMapGetter('portals/isCreatingPortal');

const state = reactive({
  name: '',
  slug: '',
  domain: '',
  logoUrl: '',
  avatarBlobId: '',
});

const rules = {
  name: { required, minLength: minLength(2) },
  slug: {
    required: helpers.withMessage(
      () => t('HELP_CENTER.CREATE_PORTAL_DIALOG.SLUG.ERROR'),
      required
    ),
    isValidSlug: helpers.withMessage(
      () => t('HELP_CENTER.CREATE_PORTAL_DIALOG.SLUG.FORMAT_ERROR'),
      isValidSlug
    ),
  },
};

const v$ = useVuelidate(rules, state);

const nameError = computed(() =>
  v$.value.name.$error ? t('HELP_CENTER.CREATE_PORTAL_DIALOG.NAME.ERROR') : ''
);

const slugError = computed(() => {
  return v$.value.slug.$errors[0]?.$message || '';
});

const isSubmitDisabled = computed(() => v$.value.$invalid);

watch(
  () => state.name,
  () => {
    state.slug = convertToCategorySlug(state.name);
  }
);

const redirectToPortal = portal => {
  emit('create', { slug: portal.slug, locale: 'en' });
};

const resetForm = () => {
  Object.keys(state).forEach(key => {
    state[key] = '';
  });
  v$.value.$reset();
};
const createPortal = async portal => {
  try {
    await store.dispatch('portals/create', portal);
    dialogRef.close();

    useTrack();
    useTrack();

    useAlert(
      t('HELP_CENTER.PORTAL_SETTINGS.API.CREATE_PORTAL.SUCCESS_MESSAGE')
    );

    resetForm();
    redirectToPortal(portal);
  } catch (error) {
    dialogRef.close();

    useAlert(
      error?.message ||
        t('HELP_CENTER.PORTAL_SETTINGS.API.CREATE_PORTAL.ERROR_MESSAGE')
    );
  }
};

const handleDialogConfirm = async () => {
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) return;

  const portal = {
    name: state.name,
    slug: state.slug,
    custom_domain: state.domain,
    blob_id: state.avatarBlobId || null,
    color: '#2781F6', // The default color is set to Chatwoot brand color
  };
  await createPortal(portal);
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="sm:max-w-md">
      <form class="grid gap-6" novalidate @submit.prevent="handleDialogConfirm">
        <DialogHeader>
          <DialogTitle>
            {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.TITLE') }}
          </DialogTitle>
          <DialogDescription>
            {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.DESCRIPTION') }}
          </DialogDescription>
        </DialogHeader>
        <FieldGroup>
          <Field :data-invalid="nameError ? true : undefined">
            <FieldLabel for="portal-name">
              {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.NAME.LABEL') }}
            </FieldLabel>
            <Input
              id="portal-name"
              v-model="state.name"
              type="text"
              :placeholder="
                t('HELP_CENTER.CREATE_PORTAL_DIALOG.NAME.PLACEHOLDER')
              "
              :aria-invalid="nameError ? 'true' : undefined"
              aria-describedby="portal-name-message"
              @blur="v$.name.$touch()"
            />
            <FieldError v-if="nameError" id="portal-name-message">
              {{ nameError }}
            </FieldError>
            <FieldDescription v-else id="portal-name-message">
              {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.NAME.MESSAGE') }}
            </FieldDescription>
          </Field>
          <Field :data-invalid="slugError ? true : undefined">
            <FieldLabel for="portal-slug">
              {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.SLUG.LABEL') }}
            </FieldLabel>
            <Input
              id="portal-slug"
              v-model="state.slug"
              type="text"
              :placeholder="
                t('HELP_CENTER.CREATE_PORTAL_DIALOG.SLUG.PLACEHOLDER')
              "
              :aria-invalid="slugError ? 'true' : undefined"
              aria-describedby="portal-slug-message"
              @input="v$.slug.$touch()"
              @blur="v$.slug.$touch()"
            />
            <FieldError v-if="slugError" id="portal-slug-message">
              {{ slugError }}
            </FieldError>
            <FieldDescription v-else id="portal-slug-message" class="break-all">
              {{ buildPortalURL(state.slug) }}
            </FieldDescription>
          </Field>
        </FieldGroup>
        <DialogFooter>
          <DialogClose as-child>
            <Button type="button" variant="outline">
              {{ t('DIALOG.BUTTONS.CANCEL') }}
            </Button>
          </DialogClose>
          <Button
            type="submit"
            :disabled="isSubmitDisabled"
            :loading="isCreatingPortal"
          >
            {{ t('HELP_CENTER.CREATE_PORTAL_DIALOG.CONFIRM_BUTTON_LABEL') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
